import { FunctionComponent } from 'react';
import { PriceCondition } from '@/models/product';
import { Stack, SvgIcon, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DeleteIcon from '@heroicons/react/24/solid/TrashIcon';
import {
    SplitIconContainer,
    IconButtonTop,
    ConditionInfoContainer,
    ConditionPreviewContainer,
    IconButtonContainer,
    SortNumberContainer,
    DeleteButtonTile,
} from '@/pageContents/productConfiguration/ProductConfigurationDetail/conditions/ConditionList';
import { BoldLabel } from '@/components/Layout/Textual';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

type PriceConditionPreviewProps = {
    condition: PriceCondition;
    onCopy: (template: PriceCondition) => void;
    onUpdate: (condition: PriceCondition) => void;
    onDelete: (condition: PriceCondition) => void;
    onPriorityChange: (
        condition: PriceCondition,
        priorityChange: 'increase' | 'decrease'
    ) => void;
    isLast: boolean;
    isFirst: boolean;
    position: number;
};

export const PriceConditionPreview: FunctionComponent<
    React.PropsWithChildren<PriceConditionPreviewProps>
> = ({
    condition,
    onPriorityChange,
    onUpdate,
    onCopy,
    onDelete,
    isFirst,
    isLast,
    position,
}) => {
    return (
        <ConditionPreviewContainer>
            <SortNumberContainer>
                <BoldLabel>{position}.</BoldLabel>
            </SortNumberContainer>
            <ConditionInfoContainer>
                <Stack
                    spacing={2}
                    sx={{
                        width: '50%',
                    }}
                >
                    <Typography>
                        <BoldLabel>Interner Name:&nbsp;</BoldLabel>
                        {condition.name}
                    </Typography>

                    {(condition.dayContingentFrom !== null ||
                        condition.dayContingentTo !== null) && (
                        <Typography>
                            <BoldLabel>
                                Kontingent Verkaufsdatum:&nbsp;
                            </BoldLabel>
                            {condition.dayContingentFrom !== null &&
                                `von ${condition.dayContingentFrom}`}{' '}
                            {condition.dayContingentTo !== null &&
                                `bis ${condition.dayContingentTo}`}
                        </Typography>
                    )}

                    {(condition.startDateContingentFrom !== null ||
                        condition.startDateContingentTo !== null) && (
                        <Typography>
                            <BoldLabel>Kontingent Startdatum:&nbsp;</BoldLabel>
                            {condition.startDateContingentFrom !== null &&
                                `von ${condition.startDateContingentFrom}`}{' '}
                            {condition.startDateContingentTo !== null &&
                                `bis ${condition.startDateContingentTo}`}
                        </Typography>
                    )}

                    {(condition.conditionContingentFrom !== null ||
                        condition.conditionContingentTo !== null) && (
                        <Typography>
                            <BoldLabel>Kontingent Regel:&nbsp;</BoldLabel>
                            {condition.conditionContingentFrom !== null &&
                                `von ${condition.conditionContingentFrom}`}{' '}
                            {condition.conditionContingentTo !== null &&
                                `bis ${condition.conditionContingentTo}`}
                        </Typography>
                    )}

                    {(condition.validityPeriodContingentFrom !== null ||
                        condition.validityPeriodContingentTo !== null) && (
                        <Typography>
                            <BoldLabel>
                                Kontingent Gültigkeitszeitraum:&nbsp;
                            </BoldLabel>
                            {condition.validityPeriodContingentFrom !== null &&
                                `von ${condition.validityPeriodContingentFrom}`}{' '}
                            {condition.validityPeriodContingentTo !== null &&
                                `bis ${condition.validityPeriodContingentTo}`}
                        </Typography>
                    )}

                    <Typography>
                        <BoldLabel>Rabatt:&nbsp;</BoldLabel>
                        {condition.priceChange?.amount}{' '}
                        {condition.priceChangeAbsolute ? 'EUR' : '%'}
                    </Typography>
                    <Typography>
                        <BoldLabel>Stornierbar:</BoldLabel>{' '}
                        {condition.blockCancellation ? 'Nein' : 'Ja'}
                    </Typography>
                </Stack>
                <Stack
                    spacing={2}
                    sx={{
                        flexDirection: 'column',
                        flexGrow: 1,
                    }}
                >
                    {(!!condition.startDateValidFrom ||
                        !!condition.startDateValidTo) && (
                        <Typography>
                            <BoldLabel>Gültigkeitszeitraum:</BoldLabel>{' '}
                            konfiguriert
                        </Typography>
                    )}

                    {(!!condition.validTo || !!condition.validFrom) && (
                        <Typography>
                            <BoldLabel>Verkaufszeitraum:</BoldLabel>{' '}
                            konfiguriert
                        </Typography>
                    )}

                    {!!condition.personTypeIds.length && (
                        <Typography>
                            <BoldLabel>Personentyp(en):</BoldLabel> konfiguriert
                        </Typography>
                    )}
                </Stack>
            </ConditionInfoContainer>
            <SplitIconContainer>
                <IconButtonTop
                    onClick={() => onUpdate(condition)}
                    disableElevation
                >
                    <EditIcon />
                </IconButtonTop>

                <IconButtonContainer
                    onClick={() => {
                        onCopy({
                            ...condition,
                            id: '',
                            name: `${condition.name} (kopie)`,
                        });
                    }}
                    disableElevation
                >
                    <ContentCopyIcon />
                </IconButtonContainer>
            </SplitIconContainer>

            <SplitIconContainer>
                <IconButtonTop
                    onClick={() => {
                        onPriorityChange(condition, 'increase');
                    }}
                    disabled={isFirst}
                    disableElevation
                >
                    <KeyboardArrowUpIcon />
                </IconButtonTop>

                <IconButtonContainer
                    onClick={() => {
                        onPriorityChange(condition, 'decrease');
                    }}
                    disabled={isLast}
                    disableElevation
                >
                    <KeyboardArrowDownIcon />
                </IconButtonContainer>
            </SplitIconContainer>
            <DeleteButtonTile onClick={() => onDelete(condition)}>
                <SvgIcon>
                    <DeleteIcon />
                </SvgIcon>
            </DeleteButtonTile>
        </ConditionPreviewContainer>
    );
};
